import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { addDays, sameDay } from "../../lib/calendar";
import { afterState, whoFilter, type MeetingsViewState } from "../../lib/meetings-view";
import { salesApi } from "../../lib/sales-api";
import type { MeetingOutcome, SalesMeeting } from "../../lib/sales-types";
import { cn } from "../ui/cn";
import { AFTER_LABELS } from "./AfterIcon";
import { clock, dayMonth } from "./MeetingRow";
import { isTyping } from "./MeetingsCalendar";

/**
 * Recent: every meeting that has happened, newest first, read like an inbox.
 *
 * Built to Gmail's pattern because it is the job Gmail's inbox does -- work
 * down a list of things that arrived, open one, deal with it, move to the next
 * -- and the reps already have the habits for it. One line a meeting; the ones
 * nothing has been done about yet are **bold**, as an unread email is, so the
 * outstanding visits stand out without a separate list to go and find.
 *
 * Today's meetings join as soon as they start. One that has not started yet
 * belongs to the calendar, and listing it here would be listing the future in a
 * view about the past.
 *
 * Thirty at a time, loaded as you scroll, and grouped under sticky day
 * headings. J and K move, Enter opens, N opens on the notes and R on a
 * follow-up -- Gmail's keys where Gmail has one.
 */
const PAGE = 30;

const FILTERS: { value: MeetingOutcome | null; label: string }[] = [
  { value: null, label: "All" },
  { value: "needs_notes", label: "Needs notes" },
  { value: "needs_follow_up", label: "Needs follow-up" },
  { value: "done", label: "Done" },
];

export function RecentFeed({
  state,
  set,
  selectedId,
  onOpen,
}: {
  state: MeetingsViewState;
  set: (patch: Record<string, string | null>) => void;
  selectedId: number | null;
  onOpen: (meeting: SalesMeeting, focus?: "notes" | "follow-up") => void;
}) {
  const [search, setSearch] = useState(state.q);
  const [cursor, setCursor] = useState(0);
  const scroller = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);

  /* The search box is local while typing and reaches the URL a beat later,
     so every keystroke is not a request and a history entry. */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (search !== state.q) set({ q: search });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, state.q, set]);

  const params = {
    when: "past" as const,
    outcome: state.filter ?? undefined,
    q: state.q || undefined,
    ...whoFilter(state.who),
  };

  const feed = useInfiniteQuery({
    /* Under the sales root so every write that invalidates the diary
       refreshes this too, but not under `salesMeetings` -- an infinite query's
       data is pages, and sharing a key with a plain list would hand one
       component the other's shape. */
    queryKey: ["sales", "meetings-feed", params],
    queryFn: ({ pageParam }) => salesApi.meetings({ ...params, limit: PAGE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.meetings.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    staleTime: 30_000,
  });

  const rows = useMemo(
    () => feed.data?.pages.flatMap((page) => page.meetings) ?? [],
    [feed.data],
  );
  const total = feed.data?.pages[0]?.total ?? 0;

  /* Load the next thirty as the bottom of the list comes into view. */
  useEffect(() => {
    const target = sentinel.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && feed.hasNextPage && !feed.isFetchingNextPage) {
          void feed.fetchNextPage();
        }
      },
      { root: scroller.current, rootMargin: "400px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [feed]);

  /* Follow the open meeting, so J after a click carries on from there. */
  useEffect(() => {
    if (selectedId === null) return;
    const index = rows.findIndex((row) => row.id === selectedId);
    if (index >= 0) setCursor(index);
  }, [selectedId, rows]);

  useEffect(() => {
    const row = scroller.current?.querySelector(`[data-index="${cursor}"]`);
    row?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  /* Gmail's keys. With a meeting open, moving opens the next one, as it does
     in Gmail's split pane. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target) || document.querySelector("[role=dialog]")) return;
      if (rows.length === 0) return;

      const key = event.key.toLowerCase();
      const moveTo = (index: number): void => {
        const next = Math.max(0, Math.min(rows.length - 1, index));
        setCursor(next);
        if (selectedId !== null) onOpen(rows[next]!);
      };

      if (key === "j" || key === "arrowdown") moveTo(cursor + 1);
      else if (key === "k" || key === "arrowup") moveTo(cursor - 1);
      else if (key === "enter" || key === "o") onOpen(rows[cursor]!);
      else if (key === "n") onOpen(rows[cursor]!, "notes");
      else if (key === "r") onOpen(rows[cursor]!, "follow-up");
      else return;
      event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rows, cursor, selectedId, onOpen]);

  const groups = groupByDay(rows);
  let index = -1;

  return (
    /* Its sticky day headings stay under the app's menus -- see the calendar. */
    <div className="isolate flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search companies, people, notes"
          aria-label="Search meetings"
          className="h-8 w-full rounded-md border border-line bg-surface px-2.5 text-sm text-content placeholder:text-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 sm:w-72"
        />
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((filter) => (
            <button
              key={filter.label}
              type="button"
              aria-pressed={state.filter === filter.value}
              onClick={() => set({ filter: filter.value })}
              className={cn(
                "h-7 rounded-full border px-3 text-xs transition-colors",
                state.filter === filter.value
                  ? "border-brand-600 bg-brand-600 font-medium text-white"
                  : "border-line bg-surface text-muted hover:bg-surface-2",
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <span className="ml-auto hidden text-[11px] text-faint lg:inline">
          J/K move · Enter open · N notes · R follow-up
        </span>
      </div>

      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
        {feed.isLoading ? (
          <p className="p-8 text-center text-sm text-faint">Loading…</p>
        ) : total === 0 ? (
          <p className="p-8 text-center text-sm text-faint">
            {state.q || state.filter ? "Nothing matches that." : "No meetings have happened yet."}
          </p>
        ) : (
          groups.map((group) => (
            <section key={group.key}>
              <h3 className="sticky top-0 z-10 border-b border-line bg-app px-4 py-1 text-[11px] font-medium uppercase tracking-wide text-faint">
                {group.label}
              </h3>
              {group.meetings.map((meeting) => {
                index += 1;
                const at = index;
                return (
                  <FeedRow
                    key={meeting.id}
                    index={at}
                    meeting={meeting}
                    active={meeting.id === selectedId}
                    cursor={at === cursor}
                    onOpen={() => {
                      setCursor(at);
                      onOpen(meeting);
                    }}
                  />
                );
              })}
            </section>
          ))
        )}
        <div ref={sentinel} className="h-8">
          {feed.isFetchingNextPage ? (
            <p className="py-2 text-center text-[11px] text-faint">Loading more…</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * One meeting, one line: status · time · company · title · who · notes · badge.
 *
 * On a phone the same facts in two lines, because a single line of seven
 * things at 375 pixels is a line of seven truncations.
 */
function FeedRow({
  meeting,
  index,
  active,
  cursor,
  onOpen,
}: {
  meeting: SalesMeeting;
  index: number;
  active: boolean;
  cursor: boolean;
  onOpen: () => void;
}) {
  const after = afterState(meeting);
  const off = meeting.status === "cancelled";
  /* Bold until something has been done about it -- Gmail's unread. */
  const unread = after === "nothing";
  const people = [
    meeting.contactName,
    ...meeting.attendees.map((person) => person.name?.split(" ")[0]),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <button
      type="button"
      data-index={index}
      onClick={onOpen}
      className={cn(
        "flex w-full min-w-0 items-start gap-2.5 border-b border-line px-4 py-2 text-left text-sm sm:items-center",
        active
          ? "bg-brand-50 dark:bg-brand-500/10"
          : unread
            ? "bg-surface hover:bg-surface-2"
            : "bg-surface-2/40 hover:bg-surface-2",
        cursor && "shadow-[inset_3px_0_0_var(--color-brand-600)]",
      )}
    >
      <StatusDot state={off ? "cancelled" : after} />

      <span
        className={cn(
          "w-11 shrink-0 tabular-nums text-muted",
          unread && "font-semibold text-content",
          off && "line-through",
        )}
      >
        {clock(meeting.startAt)}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
        <span className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              "truncate sm:w-44 sm:shrink-0",
              unread ? "font-bold text-content" : "font-medium text-content",
              off && "line-through",
            )}
          >
            {meeting.crmCompanyName ?? meeting.companyName}
          </span>
          <FollowUpBadge meeting={meeting} className="ml-auto sm:hidden" />
        </span>

        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          {meeting.subject ? (
            <span className={cn("shrink-0 truncate sm:max-w-[16rem]", unread ? "font-semibold text-content" : "text-content")}>
              {meeting.subject}
            </span>
          ) : null}
          {people ? (
            <span className="hidden shrink-0 truncate text-xs text-muted md:inline md:max-w-[12rem]">
              {meeting.subject ? "· " : ""}
              {people}
            </span>
          ) : null}
          <span className="min-w-0 flex-1 truncate text-xs text-faint">
            {meeting.notesPreview
              ? `${meeting.subject || people ? "— " : ""}${meeting.notesPreview}`
              : ""}
          </span>
        </span>
      </span>

      <FollowUpBadge meeting={meeting} className="hidden sm:inline-flex" />
    </button>
  );
}

function FollowUpBadge({ meeting, className }: { meeting: SalesMeeting; className?: string }) {
  if (meeting.status === "cancelled") {
    return (
      <span className={cn("shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-medium text-muted-on-tint", className)}>
        Cancelled
      </span>
    );
  }
  if (meeting.followUpCount > 0) {
    return (
      <span className={cn("shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300", className)}>
        Follow-up sent
      </span>
    );
  }
  if (meeting.needsFollowUp) {
    return (
      <span className={cn("shrink-0 rounded-full border border-amber-300 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-500/40 dark:text-amber-300", className)}>
        No follow-up
      </span>
    );
  }
  return null;
}

function StatusDot({ state }: { state: ReturnType<typeof afterState> | "cancelled" }) {
  const label =
    state === "cancelled" ? "Cancelled" : state ? AFTER_LABELS[state] : "Still running";
  return (
    <span
      title={label}
      aria-label={label}
      role="img"
      className={cn(
        "mt-1.5 size-2.5 shrink-0 rounded-full sm:mt-0",
        state === "done"
          ? "bg-emerald-500"
          : state === "notes" || state === "followed-up"
            ? "bg-amber-500"
            : state === "nothing"
              ? "border-2 border-red-500"
              : "bg-zinc-300 dark:bg-zinc-600",
      )}
    />
  );
}

/** "Today", "Yesterday", then "Thu 8 Oct". */
function groupByDay(rows: SalesMeeting[]) {
  const today = new Date();
  const yesterday = addDays(today, -1);
  const groups: { key: string; label: string; meetings: SalesMeeting[] }[] = [];

  for (const row of rows) {
    const date = new Date(row.startAt);
    const key = date.toDateString();
    const last = groups.at(-1);
    if (last?.key === key) {
      last.meetings.push(row);
      continue;
    }
    groups.push({
      key,
      label: sameDay(date, today) ? "Today" : sameDay(date, yesterday) ? "Yesterday" : dayMonth(row.startAt),
      meetings: [row],
    });
  }
  return groups;
}
